import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ServiceFAQ } from '@/components/sections/ServiceFAQ';
import { useJsonLd } from '@/hooks/useJsonLd';

const valuationFAQs = [
  { q: "How much does a business valuation cost?", a: "Business valuation fees vary based on the purpose, complexity, and level of report required. A calculation of value for internal planning differs from a full valuation for sale, litigation, or estate purposes. KGOB provides transparent pricing after understanding your specific needs. We serve business owners from our Charlotte, NC headquarters and remotely across the United States." },
  { q: "When do I need a business valuation?", a: "Common triggers include: planning to sell your business, bringing in or buying out a partner, estate and gift tax planning, divorce proceedings, SBA loan applications, shareholder disputes, and strategic planning to understand what drives your company's value. Even if you're not selling now, knowing your baseline value helps you make better growth decisions." },
  { q: "What methods do CPAs use to value a business?", a: "The three primary approaches are: Income Approach (based on future earnings capacity — most common for profitable businesses), Market Approach (comparing to similar businesses that have sold), and Asset Approach (based on net asset value — common for asset-heavy or holding companies). KGOB selects the most appropriate method based on your industry, size, and the purpose of the valuation." },
  { q: "How long does a business valuation take?", a: "A calculation of value for planning purposes can be completed in 2-4 weeks. A formal valuation report for sale, litigation, or tax purposes typically takes 4-8 weeks depending on the complexity of the business and availability of financial records." },
  { q: "Can a business valuation help me pay less in taxes?", a: "Yes. Valuations are critical for estate and gift tax planning (knowing fair market value for transfers), S-Corp reasonable compensation determinations, charitable contribution substantiation, and buy-sell agreement pricing. An accurate valuation can prevent overpaying gift or estate taxes by hundreds of thousands of dollars." }
];

const BusinessValuation = () => {
    useJsonLd('valuation-0', {"@context": "https://schema.org", "@type": "Service", "name": "Business Valuation Services", "description": "Professional business valuation for sales, acquisitions, partner buyouts, estate and gift tax planning, SBA loans, and strategic growth decisions. CPA-prepared valuations.", "url": "https://kgob.com/services/valuation", "provider": {"@type": "AccountingService", "name": "KGOB CPAs & Advisors", "url": "https://kgob.com", "address": {"@type": "PostalAddress", "addressLocality": "Charlotte", "addressRegion": "NC", "addressCountry": "US"}}, "serviceType": ["Business Valuation", "Company Valuation", "Partner Buyout Valuation", "Estate Tax Valuation", "M&A Valuation"], "areaServed": {"@type": "Country", "name": "United States"}});

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Helmet>
        <title>Business Valuation Services | Company Valuation for Exit & Growth | KGOB</title>
        <meta name="description" content="Business valuation services and company valuation for exit planning, succession, investments, and growth decisions from KGOB CPAs in North Carolina." />
        <link rel="canonical" href="https://kgob.com/services/valuation" />
        <meta property="og:title" content="Business Valuation Services | Company Valuation for Exit & Growth | KGOB" />
        <meta property="og:description" content="Professional business valuation for sales, acquisitions, partner buyouts, estate planning, and strategic growth decisions." />
        <meta property="og:url" content="https://kgob.com/services/valuation" />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-[#0B2240] via-[#0f2d4f] to-[#0B2240] py-20 lg:py-32">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto text-center"
            >
              <div className="flex justify-center mb-6">
                <TrendingUp className="w-16 h-16 text-[#D4AF37]" />
              </div>
              
              <h1 className="font-['Playfair_Display'] text-5xl font-bold text-[#F7F6F2] mb-6">
                Business Valuation Services
              </h1>
              
              <p className="text-xl text-[#D4AF37] italic mb-4">
                Know your value. Then make it grow.
              </p>
              
              <p className="text-lg text-[#F7F6F2]/90 mb-6">
                KGOB business valuation services deliver company valuation clarity for exit planning, succession, financing, and growth. Understand where the business stands and which levers increase value.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Button data-testid="business-valuation-button-2" asChild className="h-14 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                  <Link data-testid="business-valuation-link-2" to="/book-consultation?source=services-valuation">Request Valuation Review</Link>
                </Button>
                <Button data-testid="business-valuation-button-1" asChild variant="outline" className="h-14 px-8 border-2 border-[#F7F6F2] text-[#F7F6F2]">
                  <Link data-testid="business-valuation-link-1" to="/services">View All Services</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Keyword focus */}
        <section className="py-16 bg-white" data-testid="valuation-keyword-section">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl text-center">
            <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-[#0B2240] mb-4">
              Business valuation services and company valuation
            </h2>
            <p className="text-lg text-[#4C5A70] max-w-3xl mx-auto">
              Signed valuations for exits, succession, financing, and disputes, with growth benchmarking that shows which levers raise company value.
            </p>
          </div>
        </section>

        {/* Is This Right for You? */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-8 text-center">
              Is This Right for You?
            </h2>
            
            <Card className="p-8 border-2 border-[#E1DFD7]">
              <p className="text-[#4C5A70] mb-6">
                This service is designed for business owners who want to understand their company's value and take steps to increase it over time. You will benefit most if:
              </p>
              
              <div className="space-y-3">
                {[
                  'You want to know what your business is worth today',
                  'You are preparing for an eventual sale or succession',
                  'You want to track value drivers and benchmark against industry standards',
                  'You are considering a partner buyout or ownership restructuring',
                  'You want to align your growth strategy with value creation'
                ].map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span className="text-[#4C5A70]">{item}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </section>

        {/* What You Get */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              What You Get
            </h2>
            
            <div className="space-y-8">
              <Card className="p-8 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">Business Value Snapshots</h3>
                <p className="text-[#4C5A70] mb-4">
                  We provide estimated business valuations using industry-standard methods (SDE multiples, asset-based, etc.) to give you a sense of where your company stands.
                </p>
                <ul className="space-y-2">
                  {[
                    'Seller\'s Discretionary Earnings (SDE) calculation',
                    'Industry multiple benchmarking',
                    'Asset-based valuation for asset-heavy businesses',
                    'Valuation report with plain-language summary'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4C5A70]">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="p-8 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">Exit & Succession Readiness</h3>
                <p className="text-[#4C5A70] mb-4">
                  We help you prepare for an eventual exit or succession by identifying gaps and building a roadmap to maximize value.
                </p>
                <ul className="space-y-2">
                  {[
                    'Exit readiness assessment',
                    'Value-driver scorecard',
                    'Succession planning conversations',
                    'Integration with estate and tax planning'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4C5A70]">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="p-8 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">Benchmarking & Growth Tools</h3>
                <p className="text-[#4C5A70] mb-4">
                  We provide ongoing benchmarking and access to tools like Let\'s Talk Growth to track your progress and identify opportunities.
                </p>
                <ul className="space-y-2">
                  {[
                    'Industry benchmarking (profit margins, revenue per employee, etc.)',
                    'Key performance indicator (KPI) tracking',
                    'Access to Let\'s Talk Growth platform',
                    'Quarterly or annual value snapshots'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4C5A70]">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              How It Works
            </h2>
            
            <div className="space-y-6">
              {[
                { step: '1', title: 'Initial Assessment', desc: 'We review your financials, business model, and goals to understand your value drivers.' },
                { step: '2', title: 'Valuation Calculation', desc: 'We calculate an estimated valuation using industry-standard methods and provide a written summary.' },
                { step: '3', title: 'Value-Driver Review', desc: 'We identify the top levers that will increase your business value over time (profitability, systems, customer concentration, etc.).' },
                { step: '4', title: 'Growth Plan', desc: 'We build a roadmap with specific actions to improve your value drivers and track progress.' },
                { step: '5', title: 'Ongoing Monitoring', desc: 'We provide periodic value snapshots and benchmarking to show how your business is trending.' }
              ].map((item, i) => (
                <Card key={i} className="p-6 border-2 border-[#E1DFD7]">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                      <span className="text-[#0B2240] font-bold text-lg">{item.step}</span>
                    </div>
                    <div>
                      <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-2">{item.title}</h3>
                      <p className="text-[#4C5A70]">{item.desc}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What Changes */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              What Changes
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#0B2240]" />
                  </div>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Functional</h3>
                <p className="text-[#4C5A70] text-sm">
                  You know your business value, understand your value drivers, and have a plan to increase it over time.
                </p>
              </Card>

              <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#0B2240]" />
                  </div>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Emotional</h3>
                <p className="text-[#4C5A70] text-sm">
                  You feel confident knowing your largest asset is being managed strategically and prepared for future opportunities.
                </p>
              </Card>

              <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#0B2240]" />
                  </div>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Strategic</h3>
                <p className="text-[#4C5A70] text-sm">
                  With value clarity, you can make better decisions about growth, exit timing, and succession planning.
                </p>
              </Card>
            </div>
          </div>
        </section>

        <ServiceFAQ faqs={valuationFAQs} />

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-b from-[#0B2240] to-[#0f2d4f]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
            <h2 className="font-['Playfair_Display'] text-4xl font-bold text-[#F7F6F2] mb-6">
              Ready to Know Your Business Value?
            </h2>
            <p className="text-lg text-[#F7F6F2]/90 mb-10">
              Schedule a consultation to discuss your valuation and growth goals. We will review your financials and recommend next steps.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button data-testid="business-valuation-button-2" asChild className="h-14 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                <Link data-testid="business-valuation-link-2" to="/book-consultation?source=services-valuation">Request Valuation Review</Link>
              </Button>
              <Button data-testid="business-valuation-button-1" asChild variant="outline" className="h-14 px-8 border-2 border-[#F7F6F2] text-[#F7F6F2]">
                <Link data-testid="business-valuation-link-1" to="/services">View All Services</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default BusinessValuation;